import React, { useState } from 'react';
import { Archive, ArchiveRestore } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import Dialog from '@dashboard/components/Dialog';
import { Action } from '@dashboard/components/OrderPage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { chargeKey, recentBookingsKey, sellerBookingsKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import isDraftStatus from '@dashboard/services/status/isDraftStatus';

const PaymentsBookingArchiveDialog = ({ payment }) => {
  const [open, setOpen] = useState(false);
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const archived = payment.archivedAt != null;

  const toggleArchive = useMutation({
    mutationFn: () =>
      fetcher(
        `/sellers/${payment.seller}/charges/${payment._id}/${archived ? 'unarchive' : 'archive'}`,
        token,
        { method: 'POST' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });
      queryClient.invalidateQueries({ queryKey: sellerBookingsKey(payment.seller) });
      queryClient.invalidateQueries({ queryKey: recentBookingsKey(payment.seller) });
      toast({ title: archived ? 'Booking unarchived' : 'Booking archived', type: 'success' });
      setOpen(false);
    },
    onError: error => {
      toast({
        title: archived
          ? 'An error occurred while unarchiving the booking'
          : 'An error occurred while archiving the booking',
        description: error?.message,
        type: 'error',
      });
    },
  });

  return (
    <>
      {archived ? (
        <Action icon={ArchiveRestore} label="Unarchive booking" onClick={() => setOpen(true)} />
      ) : (
        <Action
          icon={Archive}
          label="Archive booking"
          onClick={() => setOpen(true)}
          off={isDraftStatus(payment.status)}
          title="An abandoned checkout can't be archived"
        />
      )}

      <Dialog
        title={archived ? 'Unarchive booking' : 'Archive booking'}
        description={
          archived
            ? "The booking moves back into your bookings list. Tickets, payments and statistics stay as they are, and the guest isn't notified."
            : "The booking moves out of your bookings list into the Archived filter. Tickets, payments and statistics stay as they are, and the guest isn't notified. You can unarchive it at any time."
        }
        size="md"
        open={open}
        onCancel={() => setOpen(false)}
        cancelButton={{ label: 'Cancel', appearance: 'secondary' }}
        confirmButton={{
          label: archived ? 'Unarchive booking' : 'Archive booking',
          appearance: 'primary',
          loading: toggleArchive.isPending,
          disabled: toggleArchive.isPending,
          onClick: () => toggleArchive.mutate(),
        }}
      />
    </>
  );
};

export default PaymentsBookingArchiveDialog;
